import React, { useEffect, useRef, useState } from 'react';
import Header from './Header.jsx';
import { Link, useNavigate } from 'react-router-dom';
import "../css/Entry.css";
import useFetch from './useFetch.jsx';
import { domain } from './Domain.jsx';
import useApiRequest from './useApiRequest.jsx';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ugandaDistricts } from './Districts.jsx';
import { useFilterContext } from './FilterContext.jsx';
import { filterEvents } from './EventFilter.jsx';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Footer from './Footer.jsx';
import { AnimatePresence, motion } from "framer-motion";
import Slider from './Slidder.jsx';
import MetaHelmet from './MetaHelmet.jsx';
import { ThreeDots } from 'react-loader-spinner';
import { Calendar, MapPin, Users, Heart, Eye, Share2, X } from 'lucide-react';
import { useLoadingContext } from './LoadingContext.jsx';
import EventCard from './EventCard.jsx';
import { Filter } from './Filter.jsx';
import notify from './Nots.jsx';

const Entry = () => {
    const { data, loading, setLoading:setLoadingEventDetails } = useFetch(`${domain}/event-details?exclude_passed=true`);
    const { data: advertisements, loading: loadingAdvertisements, setLoading: setLoadingAdvertisements } = useFetch(`${domain}/advertisements`);
    const { postRequest, response, setResponse, error } = useApiRequest();
    const { filterName, setFilterName, eventDate, setEventDate } = useFilterContext();
    const [filteredData, setFilteredData] = useState(data);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;
    const [show, setShow] = useState(false);
    const { setLoading } = useLoadingContext();



    const truncateString = (str) => {
        return str && str.length > 90 ? str.substring(0, 89) + "..." : str;
    };

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    useEffect(() => {
        if (loading === false && loadingAdvertisements === false) {
            setLoaded(true);
        }
    }, [loading, loadingAdvertisements]);

    useEffect(() => {
        if (loading || loadingAdvertisements) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [loading, loadingAdvertisements, setLoading]);

    useEffect(() => {
        if (filterName && data && data.length > 0) {
            const filtered = data.filter(event =>
                event.venue_name.toLowerCase().includes(filterName.toLowerCase()) ||
                event.venue_address.toLowerCase().includes(filterName.toLowerCase())
            );
            if (eventDate !== "all") {
                const filtered2 = filterEvents(eventDate, filtered);
                setFilteredData(filtered2);
            } else {
                setFilteredData(filtered);
            }
        } else {
            if (eventDate !== "all" && data && data.length > 0) {
                const filtered2 = filterEvents(eventDate, data);
                setFilteredData(filtered2);
            } else {
                setFilteredData(data);
            }
        }
        setCurrentPage(1); // Reset to first page when filters change
    }, [data, filterName, eventDate]);

    const handleFeedback = async (e) => {
        e.preventDefault();
        setFeedbackLoading(true);
        const formData = new FormData(e.target);
        const response = await postRequest(`${domain}/feedback`, formData, true);
        if (response.status === 201) {
            setFeedbackLoading(false);
            e.target.reset();
            notify("feedback submitted successfully", "success");
        }
    };

    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    const title = "Welcome to Event Plots";
    const description = "Discover and book the best event plots for all your occasions. Explore top-rated locations, view details, and plan with ease.";
    const imageUrl = "https://eventplots.com/images/logo.png";
    const url = "https://eventplots.com";

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEvents = filteredData?.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData?.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <MetaHelmet title={title} description={description} imageUrl={imageUrl} url={url} />
            
            {!loaded ? (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center max-w-md mx-4"
                    >
                        <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
                            <ThreeDots
                                visible={true}
                                height="40"
                                width="40"
                                color="white"
                                ariaLabel="three-dots-loading"
                            />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Loading Events</h3>
                        <p className="text-gray-600 text-sm text-center">
                            Please wait while we fetch the latest events for you...
                        </p>
                    </motion.div>
                </div>
            ) : (
                <>
                    <Header />
                    
                    {/* Main Content */}
                    <div className="mx-auto py-6">
                        {/* Slider Section - Untouched */}
                        {advertisements && (
                            <div className="mb-8">
                                <Slider slides={advertisements} />
                            </div>
                        )}
                        
                        {/* Header Section */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-2xl shadow-lg p-6 mb-8 mx-4"
                        >
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 px-4">
                                <div className="flex-1">
                                    <h1 className="text-lg font-bold text-gray-800 mb-2">
                                        Discover Amazing Events
                                    </h1>
                                    <p className="text-gray-600 text-sm">
                                        Current Events Happening on Our Platform
                                        {filterName !== "" && (
                                            <span className="text-orange-500 font-semibold ml-2">
                                                • {filterName}
                                            </span>
                                        )}
                                    </p>
                                    {filteredData && (
                                        <p className="text-sm text-gray-500 mt-2">
                                            {filteredData.length} event{filteredData.length !== 1 ? 's' : ''} found
                                        </p>
                                    )}
                                </div>
                                
                                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                        <Filter />
                                    <motion.button 
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleShow}
                                        className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 !text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                        </svg>
                                        Submit Feedback
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>

                        {/* Events Grid */}
                        {currentEvents && currentEvents.length > 0 ? (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8 px-4"
                            >
                                {currentEvents.map((item, index) => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <EventCard event={item} />
                                    </motion.div>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white rounded-2xl shadow-lg p-12 text-center"
                            >
                                <div className="w-24 h-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-4">No Events Available</h3>
                                <p className="text-gray-600 text-sm max-w-md mx-auto">
                                    We couldn't find any events matching your criteria at this time.
                                    Please try adjusting your filters or check back later.
                                </p>
                                <motion.button 
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => {
                                        setFilterName("");
                                        setEventDate("all");
                                    }}
                                    className="mt-6 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                                >
                                    Clear Filters
                                </motion.button>
                            </motion.div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex justify-center items-center gap-2 mt-8"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                                >
                                    Previous
                                </motion.button>
                                
                                {[...Array(totalPages).keys()].map(page => (
                                    <motion.button
                                        key={page + 1}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handlePageChange(page + 1)}
                                        className={`px-4 py-2 rounded-lg transition-all duration-200 shadow-sm ${
                                            currentPage === page + 1 
                                                ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg' 
                                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page + 1}
                                    </motion.button>
                                ))}
                                
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                                >
                                    Next
                                </motion.button>
                            </motion.div>
                        )}
                    </div>

                    {/* Feedback Modal */}
                    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg" centered>
                        <Modal.Header className="border-b-0 pb-0 bg-gradient-to-r from-orange-50 to-teal-50 rounded-t-xl relative">
                            {/* Custom Close Button - Fixed Position */}
                            <span
                                onClick={handleClose}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleClose();
                                    }
                                }}
                                role="button"
                                tabIndex="0"
                                className="absolute top-4 right-4 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-md hover:shadow-lg z-10 cursor-pointer"
                            >
                                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </span>
                            
                            <div className="flex items-center gap-2 md:gap-3 pr-8 md:pr-12">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                    <svg className="w-5 h-5 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                </div>
                                <div>
                                    <Modal.Title className="text-lg md:text-2xl font-bold text-gray-800">
                                        Share Your Feedback
                                    </Modal.Title>
                                    <p className="text-xs md:text-sm text-gray-600 mt-1">Help us improve your experience</p>
                                </div>
                            </div>
                        </Modal.Header>
                        
                        <Modal.Body className="pt-4 md:pt-6 px-4 md:px-6">
                            <form onSubmit={handleFeedback} className="space-y-4 md:space-y-6">
                                {/* Info Banner */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 md:p-5 border border-blue-100">
                                    <div className="flex items-start gap-2 md:gap-3">
                                        <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <svg className="w-3 h-3 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="text-sm md:text-base font-semibold text-blue-800 mb-1">We Value Your Opinion</h4>
                                            <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                                                Thank you for taking time to help us improve. Your feedback directly impacts how we serve you better.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-4 md:space-y-6">
                                    {/* Reason Selection */}
                                    <div className="space-y-2 md:space-y-3">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3 flex items-center gap-2">
                                            <svg className="w-4 h-4 md:w-5 md:h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                            Reason for your feedback <span className="text-red-500">*</span>
                                        </label>
                                        <select 
                                            name="reason" 
                                            className="w-full px-3 py-2 md:px-4 md:py-4 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 bg-white text-gray-700 font-medium" 
                                            required
                                        >
                                            <option value="" disabled selected>Select a reason for your feedback</option>
                                            <option value="Website Navigation Experience">Website Navigation Experience</option>
                                            <option value="Booking Experience">Booking Experience</option>
                                            <option value="Event Discovery">Event Discovery</option>
                                            <option value="Payment Process">Payment Process</option>
                                            <option value="Customer Support">Customer Support</option>
                                            <option value="General Experience">General Experience</option>
                                        </select>
                                    </div>
                                    
                                    {/* Experience Rating */}
                                    <div className="space-y-2 md:space-y-3">
                                        <label className="block text-sm font-semibold text-gray-700 mb-3 md:mb-4 flex items-center gap-2">
                                            <svg className="w-4 h-4 md:w-5 md:h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                            </svg>
                                            How do you rate your overall experience? <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 md:gap-3">
                                            {[
                                                { value: 'bad', label: 'Poor', color: 'from-red-400 to-red-500', icon: '😞', bg: 'bg-red-50', border: 'border-red-200' },
                                                { value: 'average', label: 'Fair', color: 'from-orange-400 to-orange-500', icon: '😐', bg: 'bg-orange-50', border: 'border-orange-200' },
                                                { value: 'good', label: 'Good', color: 'from-yellow-400 to-yellow-500', icon: '🙂', bg: 'bg-yellow-50', border: 'border-yellow-200' },
                                                { value: 'verygood', label: 'Very Good', color: 'from-green-400 to-green-500', icon: '😊', bg: 'bg-green-50', border: 'border-green-200' },
                                                { value: 'excellent', label: 'Excellent', color: 'from-blue-400 to-blue-500', icon: '😍', bg: 'bg-blue-50', border: 'border-blue-200' }
                                            ].map((rating) => (
                                                <label key={rating.value} className="relative cursor-pointer group">
                                                    <input 
                                                        type="radio" 
                                                        name="experience" 
                                                        required 
                                                        value={rating.value}
                                                        className="sr-only peer"
                                                    />
                                                    <div className={`p-2 md:p-4 border-2 rounded-xl text-center transition-all duration-300 peer-checked:border-orange-400 peer-checked:bg-gradient-to-r peer-checked:from-orange-400 peer-checked:to-orange-500 peer-checked:text-white peer-checked:shadow-lg group-hover:border-orange-300 group-hover:shadow-md ${rating.bg} ${rating.border}`}>
                                                        <div className="text-xl md:text-3xl mb-1 md:mb-2">{rating.icon}</div>
                                                        <div className="text-xs md:text-sm font-semibold capitalize">{rating.label}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Additional Comments */}
                                    <div className="space-y-2 md:space-y-3">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3 flex items-center gap-2">
                                            <svg className="w-4 h-4 md:w-5 md:h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            Additional Comments <span className="text-red-500">*</span>
                                        </label>
                                        <textarea 
                                            name="comment" 
                                            required 
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 resize-none font-medium" 
                                            rows="3" 
                                            placeholder="Please share your thoughts, suggestions, or any specific feedback that could help us improve..."
                                        ></textarea>
                                    </div>
                                    
                                    {/* Contact Information */}
                                    <div className="bg-gray-50 rounded-xl p-4 md:p-5 border border-gray-200">
                                        <h4 className="text-sm md:text-base font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                                            <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            Contact Information
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Full Name <span className="text-red-500">*</span>
                                                </label>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 font-medium" 
                                                    required 
                                                    name="name" 
                                                    placeholder="Enter your full name" 
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Email Address <span className="text-red-500">*</span>
                                                </label>
                                                <input 
                                                    type="email" 
                                                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 font-medium" 
                                                    required 
                                                    name="email" 
                                                    placeholder="Enter your email address" 
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 md:mt-4 space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Phone Number <span className="text-red-500">*</span>
                                            </label>
                                            <input 
                                                type="tel" 
                                                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 font-medium" 
                                                required 
                                                name="phone" 
                                                placeholder="Enter your phone number" 
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4 md:pt-6 border-t border-gray-200">
                                    <motion.button 
                                        type="button"
                                        onClick={handleClose}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="flex-1 px-4 md:px-6 py-3 md:py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center justify-center gap-2 text-sm md:text-base"
                                    >
                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Cancel
                                    </motion.button>
                                    <motion.button 
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="flex-1 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 !text-white py-3 md:py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 md:gap-3 text-sm md:text-base" 
                                        type="submit"
                                    >
                                        {feedbackLoading ? (
                                            <>
                                                <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Sending Feedback...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Send Feedback
                                            </>
                                        )}
                                    </motion.button>
                                </div>
                            </form>
                        </Modal.Body>
                    </Modal>
                    
                    <Footer />
                </>
            )}
        </div>
    );
};

export default Entry;